"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.migrateLayout = migrateLayout;
const path = __importStar(require("path"));
const dist_1 = require("../../core/dist");
const RESERVED_THREAD_SUBDIRS = new Set(['plans', 'done', 'chats', 'refs', '.archive']);
const RESERVED_WEAVE_ENTRIES = new Set(['.archive', 'refs', 'chats', 'plans', 'done']);
const pad6 = (n) => String(n).padStart(6, '0');
/**
 * Assign every item a UNIQUE ordinal. Input MUST be pre-sorted (by `sort`) so phase-2
 * tie-breaks are deterministic.
 *  - phase 0: `hard` items (a done mirroring its plan) claim their ordinal first.
 *  - phase 1: `soft` items whose ordinal is unique among the remaining soft items and
 *             still free keep it — this preserves distinct ordinals and existing gaps.
 *  - phase 2: leftover soft items (collision losers) take the smallest free ordinal.
 */
function assignOrdinals(items) {
    const used = new Set();
    const out = new Map();
    const smallestUnused = () => { let n = 1; while (used.has(n))
        n++; return n; };
    for (const it of items) {
        if (!it.hard)
            continue;
        const ord = used.has(it.desired) ? smallestUnused() : it.desired;
        used.add(ord);
        out.set(it.file, ord);
    }
    const soft = items.filter(it => !it.hard);
    const freq = new Map();
    for (const it of soft)
        freq.set(it.desired, (freq.get(it.desired) ?? 0) + 1);
    for (const it of soft) {
        if (freq.get(it.desired) === 1 && !used.has(it.desired)) {
            used.add(it.desired);
            out.set(it.file, it.desired);
        }
    }
    for (const it of items) {
        if (out.has(it.file))
            continue;
        const ord = smallestUnused();
        used.add(ord);
        out.set(it.file, ord);
    }
    return out;
}
/** Record every ≥2-way contest for the audit log (does not affect the assignment). */
function collectCollisions(out, dirRel, kind, items, assigned, nameFor) {
    const byDesired = new Map();
    for (const it of items) {
        const arr = byDesired.get(it.desired) ?? [];
        arr.push(it);
        byDesired.set(it.desired, arr);
    }
    for (const [desired, group] of byDesired) {
        if (group.length < 2)
            continue;
        out.push({
            dir: dirRel, kind, contested: desired,
            members: group.map(it => ({
                from: `${dirRel}/${it.file}`,
                to: `${dirRel}/${nameFor(assigned.get(it.file))}`,
                keptDesired: assigned.get(it.file) === desired,
            })),
        });
    }
}
async function subdirs(fs, dir) {
    const entries = await fs.readdir(dir).catch(() => []);
    const out = [];
    for (const e of entries) {
        const st = await fs.stat(path.join(dir, e)).catch(() => null);
        if (st?.isDirectory())
            out.push(e);
    }
    return out;
}
async function mdFiles(fs, dir) {
    if (!(await fs.pathExists(dir)))
        return [];
    return (await fs.readdir(dir)).filter(f => f.endsWith('.md'));
}
async function migrateLayout(input, deps) {
    const dryRun = input.dryRun ?? false;
    const loomRoot = deps.getActiveLoomRoot();
    const loomDir = path.join(loomRoot, 'loom');
    const renames = [];
    const skipped = [];
    const collisions = [];
    const rel = (p) => path.relative(loomRoot, p).split(path.sep).join('/');
    // Queue a rename after collision/idempotency checks.
    const plan = (fromAbs, toAbs) => {
        if (fromAbs === toAbs)
            return; // already canonical — no-op
        if (deps.fs.existsSync(toAbs)) {
            skipped.push({ path: rel(fromAbs), reason: `target ${rel(toAbs)} already exists` });
            return;
        }
        renames.push({ from: rel(fromAbs), to: rel(toAbs) });
    };
    if (!(await deps.fs.pathExists(loomDir))) {
        return { dryRun, renames, skipped, collisions };
    }
    for (const weave of await subdirs(deps.fs, loomDir)) {
        if (RESERVED_WEAVE_ENTRIES.has(weave))
            continue;
        const weaveDir = path.join(loomDir, weave);
        for (const thread of await subdirs(deps.fs, weaveDir)) {
            if (RESERVED_THREAD_SUBDIRS.has(thread))
                continue;
            const threadDir = path.join(weaveDir, thread);
            // idea / design singletons at the thread root.
            for (const f of await mdFiles(deps.fs, threadDir)) {
                if ((0, dist_1.isIdeaFile)(f) && f !== 'idea.md') {
                    plan(path.join(threadDir, f), path.join(threadDir, (0, dist_1.singletonFileName)('idea')));
                }
                else if ((0, dist_1.isDesignFile)(f) && f !== 'design.md') {
                    plan(path.join(threadDir, f), path.join(threadDir, (0, dist_1.singletonFileName)('design')));
                }
            }
            // plans/ — collision-aware renumber; also map planId → NEW ordinal for done mirroring.
            const plansDir = path.join(threadDir, 'plans');
            const planIdToOrdinal = new Map();
            {
                const items = [];
                const idByFile = new Map();
                for (const f of await mdFiles(deps.fs, plansDir)) {
                    const ord = (0, dist_1.planOrdinalFromFile)(f);
                    if (ord === null) {
                        skipped.push({ path: rel(path.join(plansDir, f)), reason: 'no plan ordinal in filename' });
                        continue;
                    }
                    let id, created = '';
                    try {
                        const doc = await deps.loadDoc(path.join(plansDir, f));
                        id = doc?.id;
                        created = String(doc?.created ?? '');
                    }
                    catch { /* keep going; ordinal from filename still drives the rename */ }
                    if (id)
                        idByFile.set(f, id);
                    items.push({ file: f, desired: ord, hard: false, sort: `${pad6(ord)}|${created}|${id ?? f}` });
                }
                items.sort((a, b) => (a.sort < b.sort ? -1 : a.sort > b.sort ? 1 : 0));
                const assigned = assignOrdinals(items);
                collectCollisions(collisions, rel(plansDir), 'plan', items, assigned, dist_1.planFileName);
                for (const it of items) {
                    const ord = assigned.get(it.file);
                    const id = idByFile.get(it.file);
                    if (id)
                        planIdToOrdinal.set(id, ord);
                    plan(path.join(plansDir, it.file), path.join(plansDir, (0, dist_1.planFileName)(ord)));
                }
            }
            // done/ — mirror the parent plan's NEW ordinal when it resolves (hard), else fall
            // back to the done's own filename ordinal (soft, renumbered around the hard ones).
            const doneDir = path.join(threadDir, 'done');
            {
                const items = [];
                for (const f of await mdFiles(deps.fs, doneDir)) {
                    let parentOrd = null;
                    let created = '', id = '';
                    try {
                        const doc = await deps.loadDoc(path.join(doneDir, f));
                        created = String(doc?.created ?? '');
                        id = String(doc?.id ?? '');
                        if (doc?.parent_id && planIdToOrdinal.has(doc.parent_id))
                            parentOrd = planIdToOrdinal.get(doc.parent_id);
                    }
                    catch { /* fall through to filename ordinal */ }
                    const desired = parentOrd ?? (0, dist_1.planOrdinalFromFile)(f);
                    if (desired === null) {
                        skipped.push({ path: rel(path.join(doneDir, f)), reason: 'could not resolve plan ordinal for done doc' });
                        continue;
                    }
                    const hard = parentOrd !== null;
                    items.push({ file: f, desired, hard, sort: `${hard ? '0' : '1'}|${pad6(desired)}|${created}|${id || f}` });
                }
                items.sort((a, b) => (a.sort < b.sort ? -1 : a.sort > b.sort ? 1 : 0));
                const assigned = assignOrdinals(items);
                collectCollisions(collisions, rel(doneDir), 'done', items, assigned, dist_1.doneFileName);
                for (const it of items) {
                    plan(path.join(doneDir, it.file), path.join(doneDir, (0, dist_1.doneFileName)(assigned.get(it.file))));
                }
            }
            // chats/ — {threadId}-chat-NNN.md → chat-NNN.md; bare/ordinal-less names get a fresh ordinal.
            const chatsDir = path.join(threadDir, 'chats');
            const chatTargets = [];
            for (const f of await mdFiles(deps.fs, chatsDir)) {
                let ord = (0, dist_1.chatOrdinalFromFile)(f);
                if (ord === null)
                    ord = (0, dist_1.nextOrdinal)(chatTargets, 'chat');
                const target = (0, dist_1.chatFileName)(ord);
                chatTargets.push(target);
                plan(path.join(chatsDir, f), path.join(chatsDir, target));
            }
        }
    }
    if (!dryRun) {
        for (const r of renames) {
            await deps.fs.move(path.join(loomRoot, r.from), path.join(loomRoot, r.to), { overwrite: false });
        }
    }
    return { dryRun, renames, skipped, collisions };
}
